import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Mic, MicOff, PhoneOff } from "lucide-react";
import { toast } from "sonner";
import voiceService from '../services/voiceService';
import { ProbeCall, ProbeCallState } from '../lib/probeCall';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Im Dashboard gerade gewählte Stimme — auch ungespeichert testbar. */
  voice: string;
  /** Agenten-Nummer bei Mehr-Nummern-Konten. */
  phoneNumber?: string | null;
}

/**
 * Probegespräch: dasselbe Gespräch wie am Telefon, nur im Browser.
 *
 * Der Browser gibt sich gegenüber dem Voice-Server als Twilio-Leitung aus
 * (µ-law 8 kHz, gleiche Ereignisse) — deshalb läuft hier der ECHTE Agent mit
 * echten Salondaten und echten Regeln auf `gpt-live-1`. Gesperrt ist
 * serverseitig nur, was nach außen wirkt: buchen, ändern, stornieren,
 * speichern, SMS, Weiterleitung.
 */
const VoiceTestCallDialog: React.FC<Props> = ({ open, onOpenChange, voice, phoneNumber }) => {
  const [state, setState] = useState<ProbeCallState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const callRef = useRef<ProbeCall | null>(null);

  const cleanup = useCallback(() => {
    callRef.current?.stop();
    callRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) {
      cleanup();
      setState('idle');
      setErrorMsg(null);
      setMuted(false);
    }
    return cleanup;
  }, [open, cleanup]);

  const startCall = async () => {
    setState('connecting');
    setErrorMsg(null);
    try {
      const ticket = await voiceService.createProbeTicket({ voice, phone_number: phoneNumber });
      if (!ticket.success || !ticket.ws_url) {
        throw new Error(ticket.error || 'Probegespräch konnte nicht vorbereitet werden');
      }

      const call = new ProbeCall({
        onState: setState,
        onError: (message) => setErrorMsg(message),
      });
      callRef.current = call;
      call.setMuted(false);
      await call.start(ticket.ws_url);
    } catch (err) {
      console.error('Probegespräch fehlgeschlagen:', err);
      setErrorMsg(err instanceof Error ? err.message : 'Unbekannter Fehler');
      setState('error');
      cleanup();
      toast.error('Probegespräch konnte nicht gestartet werden');
    }
  };

  const endCall = () => {
    cleanup();
    setState('ended');
  };

  const toggleMute = () => {
    const next = !muted;
    callRef.current?.setMuted(next);
    setMuted(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Probegespräch</DialogTitle>
          <DialogDescription>
            Sprechen Sie direkt mit Ihrem Telefonagenten – mit Ihren Leistungen, Preisen und
            Regeln. Termine werden in dieser Vorschau nicht gebucht, geändert oder storniert.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-6">
          <div
            className={`h-24 w-24 rounded-full flex items-center justify-center transition-colors ${
              state === 'live'
                ? 'bg-success/10 ring-4 ring-success/40 animate-pulse'
                : state === 'connecting'
                ? 'bg-primary/10 ring-4 ring-primary/40'
                : 'bg-muted'
            }`}
          >
            {state === 'connecting' ? (
              <Loader2 className="h-10 w-10 text-primary-strong animate-spin" />
            ) : muted ? (
              <MicOff className="h-10 w-10 text-muted-foreground" />
            ) : (
              <Mic className={`h-10 w-10 ${state === 'live' ? 'text-success-strong' : 'text-muted-foreground'}`} />
            )}
          </div>

          <div className="text-center text-sm text-muted-foreground min-h-[1.5rem]">
            {state === 'idle' && 'Klicken Sie auf "Gespräch starten", um zu beginnen.'}
            {state === 'connecting' && 'Verbindung wird aufgebaut…'}
            {state === 'live' && 'Verbunden – sprechen Sie ins Mikrofon.'}
            {state === 'ended' && 'Gespräch beendet.'}
            {state === 'error' && (
              <span className="text-destructive-strong">{errorMsg ?? 'Fehler beim Verbinden'}</span>
            )}
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
          <div className="flex gap-2">
            {state === 'live' && (
              <Button variant="outline" onClick={toggleMute}>
                {muted ? <Mic className="h-4 w-4 mr-2" /> : <MicOff className="h-4 w-4 mr-2" />}
                {muted ? 'Stumm aus' : 'Stumm'}
              </Button>
            )}
            {state === 'live' || state === 'connecting' ? (
              <Button
                onClick={endCall}
                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              >
                <PhoneOff className="h-4 w-4 mr-2" />
                Beenden
              </Button>
            ) : (
              <Button
                onClick={startCall}
                className="bg-[#1967D2] hover:bg-[#1558b0] text-white"
              >
                <Mic className="h-4 w-4 mr-2" />
                {state === 'ended' || state === 'error' ? 'Erneut starten' : 'Gespräch starten'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VoiceTestCallDialog;
